import dayjs from 'dayjs'
import {
  ASSET_SET_FILTER,
  AssetColumn,
  AssetColumnKind,
  AssetFilter,
  AssetQueryFilter,
  AssetQueryFilters,
  AssetSetFilterModel,
  AssetSort
} from 'models'

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss'

/* Stored timestamps are TIMESTAMP_NTZ, so the bounds go out naive — a Z would claim a zone. */
const startOfDay = (value: string): string => dayjs(value).startOf('day').format(DATE_FORMAT)
const endOfDay = (value: string): string => dayjs(value).endOf('day').format(DATE_FORMAT)

const dateFilter = (model: { type?: string; dateFrom?: string; dateTo?: string }): AssetQueryFilter | null => {
  if (model.type === 'inRange' && model.dateFrom && model.dateTo) {
    return { between_: [startOfDay(model.dateFrom), endOfDay(model.dateTo)] }
  }

  if (model.type === 'before' && model.dateFrom) return { lte_: endOfDay(model.dateFrom) }
  if (model.type === 'after' && model.dateFrom) return { gte_: startOfDay(model.dateFrom) }

  return null
}

const CHIP_DATE_FORMAT = 'MM/DD/YYYY'

export const describeDateFilter = (model: { type?: string; dateFrom?: string; dateTo?: string }): string => {
  if (!dateFilter(model) || !model.dateFrom) return ''

  const from = dayjs(model.dateFrom).format(CHIP_DATE_FORMAT)

  if (model.type === 'before') return `Before ${from}`
  if (model.type === 'after') return `After ${from}`

  return `${from} - ${dayjs(model.dateTo).format(CHIP_DATE_FORMAT)}`
}

export const describeBooleanFilter = (model: { value?: string }): string =>
  model.value === 'true' || model.value === 'false' ? model.value : ''

export interface NumberFilterModel {
  filterType?: string
  type?: string
  filter?: number | null
  filterTo?: number | null
}

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

/* AG Grid's number filter, restricted to the three range options the endpoint accepts as numbers. */
const numberFilter = (model: NumberFilterModel): AssetQueryFilter | null => {
  const { type, filter, filterTo } = model

  if (type === 'inRange' && isNumber(filter) && isNumber(filterTo)) {
    return { between_: [Math.min(filter, filterTo), Math.max(filter, filterTo)] }
  }

  if (type === 'greaterThanOrEqual' && isNumber(filter)) return { gte_: filter }
  if (type === 'lessThanOrEqual' && isNumber(filter)) return { lte_: filter }

  return null
}

export const describeNumberFilter = (model: NumberFilterModel): string => {
  const range = numberFilter(model)

  if (range?.between_) return `${range.between_[0]}–${range.between_[1]}`
  if (range?.gte_ !== undefined) return `≥ ${range.gte_}`
  if (range?.lte_ !== undefined) return `≤ ${range.lte_}`

  return ''
}

const valuePredicate =(values: string[], column: AssetColumn): AssetQueryFilter =>
  column.kind === AssetColumnKind.Array ? { array_contains_: values } : { any_: values }

const termPredicate = (term: string, column: AssetColumn): AssetQueryFilter =>
  column.kind === AssetColumnKind.Array ? { array_like_: [term] } : { contains_: [term] }

/**
 * The dropdown sends the predicate the user expressed, not the enumeration behind it. Ticking
 * values by hand is a value list; Select All over a search term is that term plus whatever was
 * unticked — which matters because a term has no 50-value limit and a column like HOSTNAME runs
 * to hundreds of thousands of distinct values.
 */
const setFilter = (model: AssetSetFilterModel, column: AssetColumn): AssetQueryFilter | null => {
  const term = model.searchTerm.trim()

  if (model.selectAll) {
    if (!term) {
      /* Every value plus the empty cells is the whole column, which needs no predicate. */
      if (!model.unchecked.length && model.noValue) return null

      const everything: AssetQueryFilter = {}

      if (model.unchecked.length) everything.not_any_ = model.unchecked
      if (model.noValue) everything.no_value_ = true
      else everything.has_value_ = true

      return everything
    }

    const filter = termPredicate(term, column)

    if (model.unchecked.length) filter.not_any_ = model.unchecked
    /* The backend ORs no_value_ with the term, so rows without a value are added, not intersected. */
    if (model.noValue) filter.no_value_ = true

    return filter
  }

  const filter: AssetQueryFilter = {}

  if (model.checked.length) Object.assign(filter, valuePredicate(model.checked, column))
  if (model.noValue) filter.no_value_ = true

  return Object.keys(filter).length ? filter : null
}

const filterForColumn = (model: any, column: AssetColumn): AssetQueryFilter | null => {
  if (!model) return null

  if (column.kind === AssetColumnKind.Timestamp) return dateFilter(model)
  if (column.kind === AssetColumnKind.Number) return numberFilter(model)

  if (column.kind === AssetColumnKind.Boolean) {
    if (model.value !== 'true' && model.value !== 'false') return null

    return { equal_: model.value === 'true' }
  }

  if (model.filterType === ASSET_SET_FILTER) return setFilter(model as AssetSetFilterModel, column)

  /* Any column the asset grid has not replaced still carries AG Grid's own set filter model. */
  const values: string[] = Array.isArray(model.values) ? model.values.filter(Boolean) : []

  if (!values.length) return null

  return valuePredicate(values, column)
}

/**
 * Two predicates on one column AND together server-side, so a shortcut can narrow a panel filter.
 * One pair is rejected outright: `no_value_` with `has_value_` is a 400, and the Other bar carries
 * `has_value_` whenever the No value bar made the top N. The bar is the later intent, and its
 * predicate already excludes the empty cells the panel tick was asking for, so the tick gives way.
 */
const merge = (target: AssetQueryFilters, column: string, filter: AssetQueryFilter) => {
  const merged: AssetQueryFilter = { ...target[column], ...filter }

  if (merged.has_value_ && merged.no_value_) delete merged.no_value_

  target[column] = merged
}

/**
 * The grid's filter model in the vocabulary the records endpoint declares per column kind.
 * Sending a predicate a column does not accept is a 400, so the column's kind picks it, never
 * the shape of the widget: sets become any_/array_contains_, booleans equal_, dates a range.
 */
export const buildQueryFilter = (
  filterModel: Record<string, unknown>,
  columns: AssetColumn[],
  shortcuts: AssetFilter[] = []
): AssetQueryFilters => {
  const result: AssetQueryFilters = {}

  for (const [name, model] of Object.entries(filterModel ?? {})) {
    const column = columns.find((item) => item.name === name)

    if (!column) continue

    const filter = filterForColumn(model, column)

    if (filter) merge(result, name, filter)
  }

  /* A shortcut's predicate is the server's own, so it is forwarded whole rather than rebuilt. */
  for (const shortcut of shortcuts) {
    for (const [column, filter] of Object.entries(shortcut.queryFilter)) merge(result, column, filter)
  }

  return result
}

/* Search is scoped to the visible columns; timestamps are matched by range and are a 400 here. */
export const searchColumnsFor = (columns: AssetColumn[]): string[] =>
  columns.filter((column) => column.searchable).map((column) => column.name)

export const buildSort = (
  sortModel: { colId: string; sort: string | null }[],
  columns: AssetColumn[]
): AssetSort | null => {
  const first = sortModel.find((item) => item.sort === 'asc' || item.sort === 'desc')

  if (!first) return null
  if (!columns.some((column) => column.name === first.colId && column.sortable)) return null

  return { column: first.colId, direction: first.sort === 'asc' ? 'ASC' : 'DESC' }
}
